/**
 * Data Agent API Client
 * Thin wrapper for the SSE streaming endpoint.
 */

const envVars = import.meta.env as unknown as { VITE_QA_JUDGE_URL?: string };
const isLocal = import.meta.env.DEV;
const API_BASE = isLocal
  ? envVars.VITE_QA_JUDGE_URL || 'http://localhost:3001'
  : '';

export interface DataAgentFilters {
  dateRange?: { days?: number; from?: string; to?: string };
  excludeInternal?: boolean;
  env?: 'dev' | 'staging' | 'prod';
}

export type DataAgentSSEEvent =
  | { type: 'text'; text: string }
  | { type: 'thinking'; text: string }
  | { type: 'tool_use'; tool: string; input: string }
  | { type: 'result'; text: string }
  | { type: 'error'; error: string }
  | { type: 'done' };

/**
 * Send a message to the data agent and stream SSE responses.
 * Calls the provided callback for each event.
 * Returns an AbortController that can be used to cancel the request.
 */
export function streamDataAgentChat(
  message: string,
  onEvent: (event: DataAgentSSEEvent) => void,
  onDone?: () => void,
  filters?: DataAgentFilters,
): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const response = await fetch(`${API_BASE}/v1/data-agent/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, ...(filters && { filters }) }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText);
        onEvent({ type: 'error', error: `HTTP ${response.status}: ${errorText}` });
        onDone?.();
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        onEvent({ type: 'error', error: 'No response body' });
        onDone?.();
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from the buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // Keep incomplete line in buffer

        let currentEvent = '';
        let currentData = '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
          } else if (line === '' && currentEvent && currentData) {
            // Empty line = end of SSE event
            try {
              const parsed = JSON.parse(currentData);

              switch (currentEvent) {
                case 'text':
                  onEvent({ type: 'text', text: parsed.text ?? '' });
                  break;
                case 'thinking':
                  onEvent({ type: 'thinking', text: parsed.text ?? '' });
                  break;
                case 'tool_use':
                  onEvent({
                    type: 'tool_use',
                    tool: parsed.tool ?? '',
                    input: parsed.input ?? '',
                  });
                  break;
                case 'result':
                  onEvent({ type: 'result', text: parsed.text ?? '' });
                  break;
                case 'error':
                  onEvent({ type: 'error', error: parsed.error ?? 'Unknown error' });
                  break;
                case 'done':
                  onEvent({ type: 'done' });
                  break;
              }
            } catch {
              // Skip malformed JSON
            }
            currentEvent = '';
            currentData = '';
          }
        }
      }

      onDone?.();
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        onEvent({
          type: 'error',
          error: err instanceof Error ? err.message : 'Network error',
        });
      }
      onDone?.();
    }
  })();

  return controller;
}
